import { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import useEnv from "../../../hooks/env";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import ContextApi, {
  BusinessUnitResponse,
  MarketSegmentResponse,
  ApplicationFieldResponse,
} from "../../../api/services/ContextApi";

type BusinessUnit = BusinessUnitResponse | null;
type MarketSegment = MarketSegmentResponse | null;
type ApplicationField = ApplicationFieldResponse | null;

interface ContextFieldsHookProps {
  applicationField: string;
  businessUnit: string;
  marketSegment: string;
}

interface ContextFieldsHookType {
  preparing: boolean;
  applicationField: ApplicationField;
  applicationFieldLoadKey: string;
  businessUnit: BusinessUnit;
  marketSegment: MarketSegment;
  marketSegmentLoadKey: string;
  loadBusinessUnits: () => Promise<BusinessUnitResponse[]>;
  loadMarketSegments: () => Promise<MarketSegmentResponse[]>;
  loadApplicationFields: () => Promise<ApplicationFieldResponse[]>;
  setApplicationField: Dispatch<SetStateAction<ApplicationField>>;
  setBusinessUnit: Dispatch<SetStateAction<BusinessUnit>>;
  setMarketSegment: Dispatch<SetStateAction<MarketSegment>>;
}

export default function useContextFields(props: ContextFieldsHookProps): ContextFieldsHookType {
  const { api } = useEnv();
  const contextApi = new ContextApi(api);

  const { handleHttpError } = useContext(ErrorSnackbarContext);

  const [preparing, setPreparing] = useState(true);

  const [businessUnit, setBusinessUnit] = useState<BusinessUnit>(null);
  const [marketSegment, setMarketSegment] = useState<MarketSegment>(null);
  const [applicationField, setApplicationField] = useState<ApplicationField>(null);

  const [initialBusinessUnit, setInitialBusinessUnit] = useState<BusinessUnit>(null);
  const [initialMarketSegment, setInitialMarketSegment] = useState<MarketSegment>(null);
  const [initialApplicationField, setInitialApplicationField] = useState<ApplicationField>(null);

  const fetchApplicationField = (ms: MarketSegment): Promise<void> => {
    if (!ms || !props.applicationField) {
      return Promise.resolve();
    }
    return contextApi
      .loadApplicationFields(ms.id)
      .then(
        (results) =>
          results.filter((result) => result.name?.trim() === props.applicationField?.trim()).pop() || {
            id: props.applicationField,
            name: props.applicationField,
            marketSegment: ms,
          },
      )
      .then((af) => {
        setApplicationField(af);
        setInitialApplicationField(af);
      });
  };

  const fetchMarketSegment = (bu: BusinessUnit): Promise<void> => {
    if (!bu || !props.marketSegment) {
      return Promise.resolve();
    }
    return contextApi
      .loadMarketSegments(bu.id)
      .then(
        (response) =>
          response.filter((ms) => ms.name === props.marketSegment).pop() || {
            id: props.marketSegment,
            name: props.marketSegment,
            businessUnit: bu,
          },
      )
      .then((ms) => {
        return fetchApplicationField(ms).then(() => {
          setMarketSegment(ms);
          setInitialMarketSegment(ms);
        });
      });
  };

  const fetchBusinessUnit = () => {
    if (!props.businessUnit) {
      setPreparing(false);
      return Promise.resolve();
    }
    return contextApi
      .loadBusinessUnits()
      .then(
        (response) =>
          response.filter((bu) => bu.name === props.businessUnit).pop() || {
            id: props.businessUnit,
            name: props.businessUnit,
          },
      )
      .then((bu) => {
        return fetchMarketSegment(bu).then(() => {
          setBusinessUnit(bu);
          setInitialBusinessUnit(bu);
          setPreparing(false);
        });
      });
  };

  useEffect(() => {
    fetchBusinessUnit().catch(handleHttpError);
  }, []);

  return {
    applicationField,
    applicationFieldLoadKey: `${marketSegment?.id}`,
    businessUnit,
    loadBusinessUnits: () => {
      return contextApi.loadBusinessUnits().then((businessUnits) => {
        return businessUnits;
      });
    },
    loadMarketSegments: () => {
      return contextApi.loadMarketSegments(businessUnit?.id).then((marketSegments) => {
        if (
          marketSegments.some((ms) => ms.id === initialMarketSegment?.id) &&
          businessUnit?.id === initialBusinessUnit?.id
        ) {
          return [initialMarketSegment, ...marketSegments] as MarketSegmentResponse[];
        }
        return marketSegments as MarketSegmentResponse[];
      });
    },
    loadApplicationFields: (): Promise<ApplicationFieldResponse[]> => {
      return contextApi.loadApplicationFields(marketSegment?.id).then((applicationFields) => {
        if (
          applicationFields.some((af) => af.id === initialApplicationField?.id) &&
          marketSegment?.id === initialMarketSegment?.id
        ) {
          return [initialApplicationField, ...applicationFields] as ApplicationFieldResponse[];
        }
        return applicationFields as ApplicationFieldResponse[];
      });
    },
    marketSegment,
    marketSegmentLoadKey: `${businessUnit?.id}`,
    preparing,
    setApplicationField,
    setBusinessUnit,
    setMarketSegment,
  };
}
